import { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Pause, Volume2, VolumeX, Home, X, RotateCcw } from 'lucide-react';
import { playTibetanBowl, playBellowsExhale, playMetronomeTick, initVoices } from '@/utils/audioUtils';
import ExerciseCopyright from './ExerciseCopyright';
import ExerciseTopLogo from './ExerciseTopLogo';

const BellowsBreathModal = ({ 
  isOpen, 
  onClose, 
  onSessionComplete 
}) => {
  // Session states
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  // Phases: ready, pre-countdown, bellows, final-exhale, max-inhale, hold, post-exhale, pause, complete
  const [phase, setPhase] = useState('ready');
  const [bellowsPhase, setBellowsPhase] = useState('exhale'); // exhale, inhale (during bellows)
  const [progress, setProgress] = useState(0);
  const [cyclesCompleted, setCyclesCompleted] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [elapsedTime, setElapsedTime] = useState(0);
  
  // Pre-start countdown (3-2-1)
  const [preCountdown, setPreCountdown] = useState(3);
  
  // Bellows count during active phase
  const [bellowsCount, setBellowsCount] = useState(0);
  
  // Capsule fill level (0-100)
  const [fillLevel, setFillLevel] = useState(50);
  
  // Settings
  const [speed, setSpeed] = useState('medium'); // slow, medium, fast
  const [bellowsDuration, setBellowsDuration] = useState(45); // seconds
  const [holdDuration, setHoldDuration] = useState(30); // seconds
  const [totalCycles, setTotalCycles] = useState(1); // number of cycles (1-5)
  const [currentCycle, setCurrentCycle] = useState(1); // current cycle number
  
  // Post-apnea settings
  const postExhaleDuration = 3; // 3 seconds exhale after hold
  const pauseDuration = 5; // 5 seconds pause
  
  // Timer for current phase
  const [phaseTimeRemaining, setPhaseTimeRemaining] = useState(0);
  const [bellowsTimeRemaining, setBellowsTimeRemaining] = useState(0);
  const [postExhaleTimeRemaining, setPostExhaleTimeRemaining] = useState(0);
  const [pauseTimeRemaining, setPauseTimeRemaining] = useState(0);
  
  // Speed configs (exhale duration, inhale duration in ms)
  const speedConfigs = {
    slow: { exhale: 800, inhale: 600, label: 'Lent' },
    medium: { exhale: 500, inhale: 400, label: 'Moyen' },
    fast: { exhale: 300, inhale: 250, label: 'Rapide' }
  };
  
  // Refs
  const soundEnabledRef = useRef(true);
  const phaseRef = useRef(phase);
  const bellowsPhaseRef = useRef(bellowsPhase);
  
  // Keep refs in sync
  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);
  
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);
  
  useEffect(() => {
    bellowsPhaseRef.current = bellowsPhase;
  }, [bellowsPhase]);
  
  // Initialize voices on mount
  useEffect(() => {
    initVoices();
    const timeout = setTimeout(() => initVoices(), 100);
    return () => clearTimeout(timeout);
  }, []);
  
  // Main exercise logic
  useEffect(() => {
    if (!isOpen || !isActive || isPaused) return;
    
    const config = speedConfigs[speed];
    let interval;
    let timeout;
    
    if (phase === 'pre-countdown') {
      // 3-2-1 countdown before starting
      if (soundEnabledRef.current) {
        playMetronomeTick();
      }
      
      interval = setInterval(() => {
        setPreCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            // Start bellows phase
            setPhase('bellows');
            setBellowsTimeRemaining(bellowsDuration);
            return 3;
          }
          if (soundEnabledRef.current) {
            playMetronomeTick();
          }
          return prev - 1;
        });
      }, 1000);
      
    } else if (phase === 'bellows') {
      // Bellows breathing phase - rapid exhale/inhale cycles
      const cycleDuration = config.exhale + config.inhale;
      let cycleElapsed = 0;
      let soundPlayed = false;
      let totalElapsed = 0;
      
      // Initialize bellows countdown
      setBellowsTimeRemaining(bellowsDuration);
      
      interval = setInterval(() => {
        cycleElapsed += 50;
        totalElapsed += 50;
        
        // Update bellows countdown every second
        const newTimeRemaining = Math.max(0, bellowsDuration - Math.floor(totalElapsed / 1000));
        setBellowsTimeRemaining(newTimeRemaining);
        
        // Determine if we're in exhale or inhale part of the bellows cycle
        if (cycleElapsed <= config.exhale) {
          // Exhale phase - capsule empties completely to 0
          const exhaleProgress = cycleElapsed / config.exhale;
          setFillLevel(50 * (1 - exhaleProgress));
          setBellowsPhase('exhale');
          
          // Play bellows exhale sound at start of exhale
          if (!soundPlayed && soundEnabledRef.current) {
            playBellowsExhale();
            soundPlayed = true;
          }
        } else {
          // Inhale phase - capsule refills to 50
          const inhaleProgress = (cycleElapsed - config.exhale) / config.inhale;
          setFillLevel(inhaleProgress * 50);
          setBellowsPhase('inhale');
        }
        
        // Complete one bellows cycle
        if (cycleElapsed >= cycleDuration) {
          cycleElapsed = 0;
          soundPlayed = false;
          setBellowsCount(prev => prev + 1);
        }
        
        // Check if bellows phase is complete
        if (totalElapsed >= bellowsDuration * 1000) {
          clearInterval(interval);
          setPhase('final-exhale');
          setFillLevel(50);
          setBellowsTimeRemaining(0);
        }
      }, 50);
      
    } else if (phase === 'final-exhale') {
      // Final complete exhale - capsule empties to 0
      let exhaleProgress = 0;
      const finalExhaleDuration = 2000; // 2 seconds
      
      interval = setInterval(() => {
        exhaleProgress += 50;
        const progress = exhaleProgress / finalExhaleDuration;
        setFillLevel(50 * (1 - progress));
        setProgress(progress * 100);
        
        if (exhaleProgress >= finalExhaleDuration) {
          clearInterval(interval);
          setPhase('max-inhale');
          setProgress(0);
          if (soundEnabledRef.current) {
            playTibetanBowl('low');
          }
        }
      }, 50);
      
    } else if (phase === 'max-inhale') {
      // Maximum inhale - capsule fills to 100
      let inhaleProgress = 0;
      const maxInhaleDuration = 4000; // 4 seconds for deep inhale
      
      interval = setInterval(() => {
        inhaleProgress += 50;
        const progress = inhaleProgress / maxInhaleDuration;
        setFillLevel(progress * 100);
        setProgress(progress * 100);
        
        if (inhaleProgress >= maxInhaleDuration) {
          clearInterval(interval);
          setPhase('hold');
          setPhaseTimeRemaining(holdDuration);
          setProgress(0);
          if (soundEnabledRef.current) {
            playTibetanBowl('high');
          }
        }
      }, 50);
      
    } else if (phase === 'hold') {
      // Breath hold phase
      interval = setInterval(() => {
        setPhaseTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            // Transition to post-exhale phase
            setPhase('post-exhale');
            setPostExhaleTimeRemaining(postExhaleDuration);
            setProgress(0);
            if (soundEnabledRef.current) {
              playTibetanBowl('low');
            }
            return 0;
          }
          return prev - 1;
        });
        setProgress(prev => prev + (100 / holdDuration));
      }, 1000);
      
    } else if (phase === 'post-exhale') {
      // Post-apnea exhale (3 seconds)
      let exhaleProgress = 0;
      const postExhaleDurationMs = postExhaleDuration * 1000;
      
      interval = setInterval(() => {
        exhaleProgress += 50;
        const progress = exhaleProgress / postExhaleDurationMs;
        setFillLevel(100 * (1 - progress)); // Empty from 100 to 0
        setPostExhaleTimeRemaining(Math.ceil((postExhaleDurationMs - exhaleProgress) / 1000));
        
        if (exhaleProgress >= postExhaleDurationMs) {
          clearInterval(interval);
          setPhase('pause');
          setPauseTimeRemaining(pauseDuration);
          setFillLevel(0);
          if (soundEnabledRef.current) {
            playTibetanBowl('high');
          }
        }
      }, 50);
      
    } else if (phase === 'pause') {
      // Pause phase (5 seconds countdown)
      interval = setInterval(() => {
        setPauseTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            setCyclesCompleted(c => c + 1);
            
            // Check if there are more cycles to do
            if (currentCycle < totalCycles) {
              // Start next cycle
              setCurrentCycle(c => c + 1);
              setPhase('bellows');
              setBellowsPhase('exhale');
              setBellowsCount(0);
              setFillLevel(50);
              setBellowsTimeRemaining(bellowsDuration);
              if (soundEnabledRef.current) {
                playTibetanBowl('low');
              }
            } else {
              // All cycles complete
              setPhase('complete');
              if (soundEnabledRef.current) {
                playTibetanBowl('low');
              }
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
      if (timeout) clearTimeout(timeout);
    };
  }, [isOpen, isActive, isPaused, phase, speed, bellowsDuration, holdDuration, currentCycle, totalCycles, postExhaleDuration, pauseDuration]);
  
  // Track total elapsed time
  useEffect(() => {
    if (!isOpen || !isActive || isPaused || phase === 'ready' || phase === 'complete') return;
    
    const interval = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isOpen, isActive, isPaused, phase]);

  const startSession = () => {
    setElapsedTime(0);
    setIsActive(true);
    setIsPaused(false);
    setPhase('pre-countdown'); // Start with 3-2-1 countdown
    setPreCountdown(3);
    setBellowsPhase('exhale');
    setProgress(0);
    setCyclesCompleted(0);
    setBellowsCount(0);
    setFillLevel(50);
    setPhaseTimeRemaining(0);
    setBellowsTimeRemaining(bellowsDuration);
    setCurrentCycle(1);
  };

  const stopSession = useCallback((saveSession = false) => {
    if (saveSession && onSessionComplete && elapsedTime > 10) {
      onSessionComplete({
        patternId: 'bellows-breath',
        patternName: 'Respiration du crâne qui brille',
        durationSeconds: elapsedTime,
        cyclesCompleted: cyclesCompleted
      });
    }
    
    setIsActive(false);
    setIsPaused(false);
    setPhase('ready');
    setBellowsPhase('exhale');
    setProgress(0);
    setCyclesCompleted(0);
    setBellowsCount(0);
    setElapsedTime(0);
    setFillLevel(50);
    setPhaseTimeRemaining(0);
  }, [onSessionComplete, elapsedTime, cyclesCompleted]);

  // Reset session (go back to settings without saving)
  const resetSession = () => {
    setIsActive(false);
    setIsPaused(false);
    setPhase('ready');
    setBellowsPhase('exhale');
    setProgress(0);
    setCyclesCompleted(0);
    setBellowsCount(0);
    setElapsedTime(0);
    setFillLevel(50);
    setPhaseTimeRemaining(0);
    setPreCountdown(3);
  };

  const handleClose = () => {
    stopSession(elapsedTime > 10);
    onClose();
  };
  
  const restartCycle = () => {
    setPhase('bellows');
    setBellowsPhase('exhale');
    setProgress(0);
    setBellowsCount(0);
    setFillLevel(50);
    setPhaseTimeRemaining(0);
  };

  // Get phase instruction text
  const getPhaseText = () => {
    switch (phase) {
      case 'pre-countdown':
        return 'Préparez-vous...';
      case 'bellows':
        return bellowsPhase === 'exhale' ? 'Expirez fort !' : 'Relâchez';
      case 'final-exhale':
        return 'Videz complètement';
      case 'max-inhale':
        return 'Inspirez à fond';
      case 'hold':
        return 'Retenez';
      case 'post-exhale':
        return 'Expirez lentement';
      case 'pause':
        return 'Pause';
      case 'complete':
        return 'Cycle terminé !';
      default:
        return 'Prêt';
    }
  };
  
  // Get current timer value based on phase
  const getCurrentTimer = () => {
    switch (phase) {
      case 'bellows':
        return bellowsTimeRemaining;
      case 'hold':
        return phaseTimeRemaining;
      case 'post-exhale':
        return postExhaleTimeRemaining;
      case 'pause':
        return pauseTimeRemaining;
      default:
        return 0;
    }
  };
  
  // Get capsule color - consistent cyan/blue theme
  const getCapsuleColor = () => {
    return {
      primary: 'linear-gradient(to top, #0891b2, #06b6d4, #22d3ee)',
      glow: 'rgba(6, 182, 212, 0.5)',
      accent: 'rgba(6, 182, 212, 0.4)'
    };
  };

  if (!isOpen) return null;
  
  const capsuleColors = getCapsuleColor();

  return (
    <div 
      className="fixed inset-0 z-[60] flex items-center justify-center overflow-hidden"
      style={{ 
        background: 'linear-gradient(135deg, #1e3a5f 0%, #0c4a6e 30%, #0369a1 60%, #0284c7 100%)'
      }}
    >
      <ExerciseCopyright />
      {/* Floating Navigation Buttons */}
      <div className="absolute top-4 left-4 z-20">
        <button
          onClick={handleClose}
          className="p-3 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full transition-colors"
          title="Retour aux exercices"
        >
          <Home size={20} className="text-white" />
        </button>
      </div>
      
      <div className="absolute top-4 right-4 z-20">
        <button
          onClick={handleClose}
          className="p-3 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full transition-colors"
          title="Fermer"
        >
          <X size={20} className="text-white" />
        </button>
      </div>

        <ExerciseTopLogo />

      {/* Timer / Info - Below the Play/Pause buttons on mobile, top center on desktop */}
      {phase !== 'ready' && phase !== 'complete' && (
        <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 z-20 max-w-[85%] lg:bottom-auto lg:top-4 lg:max-w-none">
          <div className="bg-white/20 backdrop-blur-md rounded-2xl px-6 py-2 text-center">
            {phase === 'pre-countdown' && (
              <div className="flex flex-col items-center">
                <span className="text-xs text-white/70 uppercase tracking-wide mb-1">
                  Préparez-vous
                </span>
                <span className="text-3xl sm:text-4xl font-bold text-white animate-pulse" style={{ fontFamily: 'system-ui' }}>
                  {preCountdown}
                </span>
              </div>
            )}
            {phase === 'bellows' && (
              <div className="flex flex-col items-center">
                {totalCycles > 1 && (
                  <span className="text-xs text-white/70 uppercase tracking-wide mb-1">
                    Cycle {currentCycle}/{totalCycles}
                  </span>
                )}
                <span className="text-2xl sm:text-3xl font-bold text-white">
                  {bellowsCount} expirations
                </span>
              </div>
            )}
            {(phase === 'final-exhale' || phase === 'max-inhale') && (
              <div className="flex flex-col items-center">
                <span className="text-lg sm:text-xl font-bold text-white">
                  {phase === 'final-exhale' ? 'Expiration complète' : 'Inspiration maximale'}
                </span>
                {totalCycles > 1 && (
                  <span className="text-xs text-white/70 mt-0.5">
                    Cycle {currentCycle}/{totalCycles}
                  </span>
                )}
              </div>
            )}
            {phase === 'hold' && (
              <div className="flex flex-col items-center">
                <span className="text-sm text-white/70 uppercase tracking-wide">
                  Apnée {totalCycles > 1 ? `(Cycle ${currentCycle}/${totalCycles})` : ''}
                </span>
              </div>
            )}
            {phase === 'post-exhale' && (
              <div className="flex flex-col items-center">
                <span className="text-lg sm:text-xl font-bold text-white">
                  Expirez lentement
                </span>
                {totalCycles > 1 && (
                  <span className="text-xs text-white/70 mt-0.5">
                    Cycle {currentCycle}/{totalCycles}
                  </span>
                )}
              </div>
            )}
            {phase === 'pause' && (
              <div className="flex flex-col items-center">
                <span className="text-lg sm:text-xl font-bold text-white">
                  Pause
                </span>
                {totalCycles > 1 && (
                  <span className="text-xs text-white/70 mt-0.5">
                    Cycle {currentCycle}/{totalCycles}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="relative z-10 w-full h-full overflow-y-auto px-4 pt-20 sm:pt-24 pb-8 flex flex-col lg:flex-row lg:items-center lg:justify-center lg:pb-8">
        <div className="flex flex-col items-center my-auto gap-6 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:gap-10 lg:items-center w-full">
          
          {/* Left Panel - Settings */}
          <div className="w-full max-w-xs lg:w-72 flex-shrink-0 order-3 lg:order-none lg:col-start-1 lg:row-start-1 lg:justify-self-end">
            {(!isActive || isPaused || phase === 'complete') && (
              <div className="bg-white/90 backdrop-blur rounded-2xl px-4 sm:px-6 py-4 sm:py-5 shadow-lg">
                <h3 className="font-semibold text-slate-700 mb-3">
                  {phase === 'complete' ? 'Cycle terminé !' : isPaused ? 'Pause - Réglages' : 'Réglages'}
                </h3>
                
                {phase === 'complete' && (
                  <div className="mb-4 p-3 bg-green-100 rounded-xl text-center">
                    <p className="text-green-700 font-medium">
                      Bravo ! {cyclesCompleted} cycle{cyclesCompleted > 1 ? 's' : ''} terminé{cyclesCompleted > 1 ? 's' : ''}
                    </p>
                    <p className="text-green-600 text-sm">
                      {totalCycles > 1 ? `${totalCycles} × ` : ''}{bellowsDuration}s expirations + {holdDuration}s apnée
                    </p>
                  </div>
                )}
                
                <div className="flex flex-col gap-4 sm:gap-5">
                  {/* Speed */}
                  <div className="flex flex-col gap-2">
                    <span className="text-slate-600 font-medium text-sm sm:text-base">Vitesse</span>
                    <div className="flex gap-2">
                      {Object.entries(speedConfigs).map(([key, config]) => (
                        <button
                          key={key}
                          onClick={() => setSpeed(key)}
                          className={`flex-1 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                            speed === key
                              ? 'bg-cyan-500 text-white shadow-md'
                              : 'bg-white/70 text-slate-600 hover:bg-white border border-slate-200'
                          }`}
                        >
                          {config.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Bellows Duration */}
                  <div className="flex flex-col gap-2">
                    <span className="text-slate-600 font-medium text-sm sm:text-base">Durée expirations</span>
                    <div className="flex gap-2 flex-wrap">
                      {[30, 45, 60].map((sec) => (
                        <button
                          key={sec}
                          onClick={() => setBellowsDuration(sec)}
                          className={`px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                            bellowsDuration === sec
                              ? 'bg-cyan-500 text-white shadow-md'
                              : 'bg-white/70 text-slate-600 hover:bg-white border border-slate-200'
                          }`}
                        >
                          {sec < 60 ? `${sec}s` : '1min'}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Hold Duration */}
                  <div className="flex flex-col gap-2">
                    <span className="text-slate-600 font-medium text-sm sm:text-base">Durée apnée</span>
                    <div className="grid grid-cols-3 gap-2">
                      {[15, 30, 45, 60, 90].map((sec) => (
                        <button
                          key={sec}
                          onClick={() => setHoldDuration(sec)}
                          className={`px-2 py-2 rounded-xl text-xs sm:text-sm font-medium transition-all ${
                            holdDuration === sec
                              ? 'bg-sky-600 text-white shadow-md'
                              : 'bg-white/70 text-slate-600 hover:bg-white border border-slate-200'
                          }`}
                        >
                          {sec < 60 ? `${sec}s` : sec === 60 ? '1min' : "1'30"}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Number of Cycles */}
                  <div className="flex flex-col gap-2">
                    <span className="text-slate-600 font-medium text-sm sm:text-base">Nombre de cycles</span>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((num) => (
                        <button
                          key={num}
                          onClick={() => setTotalCycles(num)}
                          className={`flex-1 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                            totalCycles === num
                              ? 'bg-indigo-500 text-white shadow-md'
                              : 'bg-white/70 text-slate-600 hover:bg-white border border-slate-200'
                          }`}
                        >
                          {num}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                
                {phase === 'complete' && (
                  <button
                    onClick={restartCycle}
                    className="w-full mt-4 px-4 py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-medium rounded-xl transition-colors"
                  >
                    Recommencer un cycle
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Center Column - Capsule Visualization with Timer Circle */}
          <div className="flex flex-col items-center order-1 lg:order-none lg:col-start-2 lg:row-start-1">
            {/* Capsule and Timer Container */}
            <div className="flex items-center gap-6">
              {/* Single Capsule */}
              <div className="flex flex-col items-center">
                <div 
                  className={`relative w-24 sm:w-28 h-64 sm:h-80 rounded-full overflow-hidden backdrop-blur-sm border-2 shadow-2xl transition-all duration-300 ${
                    phase === 'bellows' 
                      ? 'bg-white/15 border-cyan-400/60' 
                      : phase === 'hold'
                        ? 'bg-white/15 border-violet-400/60'
                        : phase === 'post-exhale' || phase === 'pause'
                          ? 'bg-white/15 border-emerald-400/60'
                          : 'bg-white/10 border-cyan-400/50'
                  }`}
                >
                {/* Background glow effect */}
                <div 
                  className="absolute inset-0 rounded-full opacity-40 transition-all duration-300"
                  style={{
                    background: `radial-gradient(ellipse at center bottom, ${capsuleColors.accent}, transparent 70%)`
                  }}
                />
                
                {/* Animated fill */}
                <div 
                  className="absolute bottom-0 left-0 right-0 rounded-b-full transition-all ease-out"
                  style={{
                    height: `${fillLevel}%`,
                    background: capsuleColors.primary,
                    boxShadow: `0 0 30px ${capsuleColors.glow}, inset 0 0 20px ${capsuleColors.glow}`,
                    transitionDuration: phase === 'bellows' ? '50ms' : '100ms'
                  }}
                >
                  {/* Bubbles inside */}
                  <div className="absolute inset-0 overflow-hidden rounded-b-full">
                    {[...Array(5)].map((_, i) => (
                      <div
                        key={i}
                        className="absolute rounded-full bg-white/40 animate-pulse"
                        style={{
                          width: `${4 + (i % 3) * 2}px`,
                          height: `${4 + (i % 3) * 2}px`,
                          left: `${15 + (i * 15)}%`,
                          bottom: `${10 + (i * 18)}%`,
                          animationDelay: `${i * 0.15}s`,
                          animationDuration: `${0.8 + (i % 2) * 0.3}s`
                        }}
                      />
                    ))}
                  </div>
                  
                  {/* Top surface shine */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-2 rounded-full bg-white/50 blur-sm" />
                </div>
                
                {/* Glass reflection overlay */}
                <div 
                  className="absolute inset-0 rounded-full pointer-events-none"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 50%, rgba(0,0,0,0.1) 100%)'
                  }}
                />
                
                {/* Side highlight */}
                <div className="absolute left-2 top-4 bottom-4 w-2.5 rounded-full bg-white/15 blur-sm" />
                
                {/* Measurement markers */}
                <div className="absolute right-3 top-4 bottom-4 flex flex-col justify-between">
                  {[...Array(5)].map((_, i) => (
                    <div 
                      key={i}
                      className="w-2 h-0.5 bg-white/30 rounded-full"
                    />
                  ))}
                </div>
                
                {/* Active glow ring for bellows */}
                {phase === 'bellows' && bellowsPhase === 'exhale' && (
                  <div 
                    className="absolute -inset-1 rounded-full animate-pulse"
                    style={{
                      boxShadow: '0 0 25px rgba(6, 182, 212, 0.7), 0 0 50px rgba(6, 182, 212, 0.4)'
                    }}
                  />
                )}
              </div>
              </div>
              
              {/* Jade Timer Circle - visible during timed phases */}
              {isActive && (phase === 'bellows' || phase === 'hold' || phase === 'post-exhale' || phase === 'pause') && (
                <div 
                  className="flex flex-col items-center justify-center w-28 h-28 sm:w-36 sm:h-36 rounded-full shadow-2xl"
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
                    boxShadow: '0 0 30px rgba(16, 185, 129, 0.5), inset 0 2px 10px rgba(255,255,255,0.2)'
                  }}
                >
                  <span className="text-3xl sm:text-5xl font-bold text-white" style={{ fontFamily: 'system-ui', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                    {getCurrentTimer()}
                  </span>
                  <span className="text-xs sm:text-sm text-white/80 uppercase tracking-wider mt-1">
                    {phase === 'bellows' ? 'sec' : phase === 'hold' ? 'apnée' : phase === 'post-exhale' ? 'expir' : 'pause'}
                  </span>
                </div>
              )}
            </div>

            {/* Phase indicator */}
            <div className="mt-6 bg-white/80 backdrop-blur rounded-xl px-6 py-3 shadow-lg text-center max-w-xs">
              <span className={`text-lg sm:text-xl font-bold ${
                phase === 'post-exhale' || phase === 'pause' ? 'text-emerald-600' : 'text-cyan-600'
              }`}>
                {getPhaseText()}
              </span>
              {isActive && phase !== 'complete' && (
                <div className="text-sm text-slate-500 mt-1">
                  {phase === 'bellows' && `${speedConfigs[speed].label} - ${bellowsDuration}s`}
                  {phase === 'hold' && `Apnée inspiratoire`}
                  {phase === 'final-exhale' && 'Expiration complète'}
                  {phase === 'max-inhale' && 'Inspiration maximale'}
                  {phase === 'post-exhale' && 'Expiration 3s'}
                  {phase === 'pause' && 'Repos 5s'}
                </div>
              )}
            </div>
            
            {/* Instructions (when not active) */}
            {!isActive && phase !== 'complete' && (
              <div className="mt-4 bg-white/70 backdrop-blur rounded-xl px-4 py-3 shadow-md max-w-sm text-center">
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  <strong>Crâne qui brille (Kapalabhati)</strong> : Expirez puissamment en contractant le ventre, 
                  puis relâchez pour une inspiration passive. Terminez par une grande inspiration, une apnée, puis expirez et faites une pause.
                </p>
              </div>
            )}
          </div>

          {/* Right Panel - Controls */}
          <div className="w-full max-w-xs lg:w-64 flex-shrink-0 flex justify-center lg:justify-start order-2 lg:order-none lg:col-start-3 lg:row-start-1 lg:justify-self-start">
            <div className="flex flex-row lg:flex-col gap-3 sm:gap-4 exercise-controls-mobile-fixed">
              <button
                onClick={() => {
                  if (!isActive || phase === 'complete') {
                    startSession();
                  } else {
                    setIsPaused(!isPaused);
                  }
                }}
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                style={{ border: '3px solid rgba(6,182,212,0.5)' }}
                data-testid="bellows-play-btn"
              >
                {!isActive || isPaused || phase === 'complete' ? (
                  <Play size={28} className="text-slate-600 ml-1 sm:w-9 sm:h-9" />
                ) : (
                  <Pause size={28} className="text-slate-600 sm:w-9 sm:h-9" />
                )}
              </button>

              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                style={{ border: '3px solid rgba(6,182,212,0.5)' }}
                data-testid="bellows-sound-btn"
              >
                {soundEnabled ? (
                  <Volume2 size={22} className="text-slate-600 sm:w-7 sm:h-7" />
                ) : (
                  <VolumeX size={22} className="text-slate-600 sm:w-7 sm:h-7" />
                )}
              </button>
              
              {/* Reset button - only show when active */}
              {isActive && phase !== 'ready' && (
                <button
                  onClick={resetSession}
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                  style={{ border: '3px solid rgba(239, 68, 68, 0.5)' }}
                  data-testid="bellows-reset-btn"
                  title="Réinitialiser"
                >
                  <RotateCcw size={22} className="text-red-500 sm:w-7 sm:h-7" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BellowsBreathModal;
